"use client";

import { type LucideIcon, Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { type ReactNode, useSyncExternalStore } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "src/components/ui/tooltip";
import { cn } from "src/lib/utils";

type ThemeValue = "light" | "dark" | "system";

type ThemeOption = {
  value: ThemeValue;
  label: string;
  icon: LucideIcon;
  tooltip: string;
};

const themeOptions = [
  { value: "light", label: "Light", icon: Sun, tooltip: "Use light mode" },
  { value: "dark", label: "Dark", icon: Moon, tooltip: "Use dark mode" },
  { value: "system", label: "System", icon: Monitor, tooltip: "Match system theme" },
] as const satisfies readonly ThemeOption[];

type ThemeToggleVariant = "menu" | "compact";

type ThemeToggleProps = {
  variant?: ThemeToggleVariant;
  className?: string;
};

const variantStyles: Record<
  ThemeToggleVariant,
  {
    wrapper: string;
    label?: string;
    group: string;
    button: string;
    active: string;
    inactive: string;
  }
> = {
  menu: {
    wrapper: "px-4 py-2",
    label: "mb-2 text-sm text-gray-500 dark:text-gray-400",
    group: "flex gap-1 rounded-lg bg-gray-100 dark:bg-gray-800 p-1",
    button:
      "flex-1 flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors",
    active: "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm",
    inactive: "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300",
  },
  compact: {
    wrapper: "flex flex-col items-end gap-1.5",
    group:
      "inline-flex items-center gap-0.5 rounded-full border border-dark-10/90 bg-white/90 p-1 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-950/90",
    button:
      "flex h-9 w-9 items-center justify-center rounded-full text-xs font-medium transition-colors",
    active: "bg-purple-100 text-white shadow-sm dark:bg-purple-500 dark:text-white",
    inactive:
      "text-dark-60 dark:text-gray-400 hover:bg-purple-5 hover:text-dark-100 dark:hover:bg-gray-800 dark:hover:text-gray-200",
  },
};

const emptySubscribe = () => () => {};

function useIsMounted() {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
}

function getSelectedTheme(theme: string | undefined, isMounted: boolean): ThemeValue | undefined {
  if (!isMounted) {
    return undefined;
  }

  return theme === "light" || theme === "dark" ? theme : "system";
}

export function ThemeToggle({ variant = "menu", className }: ThemeToggleProps) {
  const { theme, setTheme } = useTheme();
  const isMounted = useIsMounted();
  const styles = variantStyles[variant];
  const selectedTheme = getSelectedTheme(theme, isMounted);

  if (variant === "compact") {
    return (
      <CompactThemeToggle
        className={className}
        isMounted={isMounted}
        onSelect={setTheme}
        selectedTheme={selectedTheme}
        styles={styles}
      />
    );
  }

  return (
    <MenuThemeToggle
      className={className}
      isMounted={isMounted}
      onSelect={setTheme}
      selectedTheme={selectedTheme}
      styles={styles}
    />
  );
}

type ThemeToggleStyles = (typeof variantStyles)[ThemeToggleVariant];

type ThemeToggleRenderProps = {
  className?: string;
  isMounted: boolean;
  onSelect: (value: ThemeValue) => void;
  selectedTheme?: ThemeValue;
  styles: ThemeToggleStyles;
};

function MenuThemeToggle({
  className,
  isMounted,
  onSelect,
  selectedTheme,
  styles,
}: ThemeToggleRenderProps) {
  return (
    <div className={cn(styles.wrapper, className)}>
      {styles.label ? <p className={styles.label}>Theme</p> : null}
      <div className={styles.group} role="group" aria-label="Theme selection">
        {themeOptions.map((option) => (
          <ThemeButton
            key={option.value}
            isMounted={isMounted}
            onSelect={onSelect}
            option={option}
            selectedTheme={selectedTheme}
            styles={styles}
          >
            <span>{option.label}</span>
          </ThemeButton>
        ))}
      </div>
    </div>
  );
}

function CompactThemeToggle({
  className,
  isMounted,
  onSelect,
  selectedTheme,
  styles,
}: ThemeToggleRenderProps) {
  return (
    <div className={cn(styles.wrapper, className)}>
      <div className={styles.group} role="group" aria-label="Theme selection">
        {themeOptions.map((option) => (
          <Tooltip key={option.value}>
            <TooltipTrigger asChild>
              <ThemeButton
                isMounted={isMounted}
                onSelect={onSelect}
                option={option}
                selectedTheme={selectedTheme}
                styles={styles}
              >
                <span className="sr-only">{option.label}</span>
              </ThemeButton>
            </TooltipTrigger>
            <TooltipContent side="bottom">{option.tooltip}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

function ThemeButton({
  children,
  isMounted,
  onSelect,
  option,
  selectedTheme,
  styles,
}: {
  children: ReactNode;
  isMounted: boolean;
  onSelect: (value: ThemeValue) => void;
  option: ThemeOption;
  selectedTheme?: ThemeValue;
  styles: ThemeToggleStyles;
}) {
  const Icon = option.icon;
  const isSelected = selectedTheme === option.value;

  return (
    <button
      type="button"
      onClick={() => onSelect(option.value)}
      disabled={!isMounted}
      aria-label={`${option.label} theme`}
      aria-pressed={isSelected}
      className={cn(styles.button, isSelected ? styles.active : styles.inactive)}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}
